import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  TextField,
  InputAdornment,
  TableHead,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BusinessListViewModel } from "./business-list-view-model";
import { useAuth } from "../auth/AuthContext";
import { BusinessListModel } from "src/types/models/business";
import SCLabel from "src/components/atoms/sc-label/sc-label";
import BusinessViewer from "../business-viewer/business-viewer";
import LoadingModal from "src/components/molecules/loading-modal/loading-modal";
import { ReactComponent as EyeIcon } from "../../../assets/eyeIcon.svg";

const BusinessList: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<BusinessListModel[]>([]);
  const [filteredData, setFilteredData] = useState<BusinessListModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10); // Initial rows per page
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewerDialog, setOpenViewerDialog] = useState(false);
  const [business, setBusiness] = useState<BusinessListModel | null>(null); // State for BusinessViewer
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(
    null
  );
  const [selectedRow, setSelectedRow] = useState<BusinessListModel | null>(
    null
  );
  const viewModel = useRef<BusinessListViewModel | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (user && user.accessToken) {
        viewModel.current = new BusinessListViewModel(user.accessToken);
        const response = await viewModel.current?.fetchList();
        setData(response);
        setFilteredData(response); // Initialize filtered data
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = data;

    if (filterName) {
      filtered = filtered.filter((item) =>
        item.name.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    if (filterStatus) {
      filtered = filtered.filter((item) =>
        item.status.toLowerCase().includes(filterStatus.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [filterName, filterStatus]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when rows per page change
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedRow(null);
    setActionType(null);
  };

  const handleViewBusiness = (business: BusinessListModel) => {
    setBusiness(business);
    setOpenViewerDialog(true);
  };

  const handleCloseViewer = async () => {
    setBusiness(null);
    setOpenViewerDialog(false);
    await fetchData();
  };

  const handleConfirmAction = () => {
    if (selectedRow && actionType) {
      if (actionType === "approve") {
        console.log(`Approved: ${selectedRow.name}`);
      } else if (actionType === "reject") {
        console.log(`Rejected: ${selectedRow.name}`);
      }
    }
    handleCloseDialog();
  };

  return (
    <Box
      className="main-content"
      sx={{
        flexGrow: 1,
        padding: 3,
        margin: `24px`,
        transition: "margin-left 0.3s ease",
        backgroundColor: "#2C2C2C",
        color: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 2,
          backgroundColor: "#1E1E1E",
          padding: 2,
          borderRadius: 1,
        }}
      >
        <SCLabel fontSize={48} color="#FFFFFF">
          Business List
        </SCLabel>
        <Tooltip title="Refresh">
          <IconButton
            size="medium"
            color="inherit"
            onClick={fetchData}
            sx={{
              border: "1px solid #FFFFFF",
              borderRadius: "4px",
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          backgroundColor: "#1E1E1E",
          borderRadius: 1,
          padding: 2,
        }}
      >
        <Table
          sx={{
            minWidth: 650,
            borderCollapse: "collapse",
            color: "#FFFFFF",
            "& .MuiTableCell-root": {
              borderBottom: "1px solid #3F3F3F",
              color: "#FFFFFF",
            },
          }}
          aria-label="business table"
        >
          <TableHead>
            <TableRow>
              <TableCell>
                <SCLabel color="#fff" fontSize={24}>
                  BUSINESS LOGO
                </SCLabel>
              </TableCell>
              <TableCell>
                <SCLabel color="#fff" fontSize={24}>
                  BUSINESS NAME
                </SCLabel>
              </TableCell>
              <TableCell>
                <SCLabel color="#fff" fontSize={24}>
                  STATUS
                </SCLabel>
              </TableCell>
              <TableCell>
                <SCLabel color="#fff" fontSize={24}>
                  ACTIONS
                </SCLabel>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>
                <TextField
                  variant="filled"
                  size="medium"
                  placeholder="Filter by Business Name"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ marginTop: "auto", marginBottom: "auto" }}
                      ></InputAdornment>
                    ),
                    style: {
                      backgroundColor: "#2C2C2C",
                      color: "#FFFFFF",
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-input": {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    },
                  }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  variant="filled"
                  size="medium"
                  placeholder="Filter by Status"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        sx={{ marginTop: "auto", marginBottom: "auto" }}
                      ></InputAdornment>
                    ),
                    style: {
                      backgroundColor: "#2C2C2C",
                      color: "#FFFFFF",
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                    },
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiInputBase-input": {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    },
                  }}
                />
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow key={index} className="hover:bg-gray-950">
                  <TableCell>
                    <div className="w-full flex items-center justify-center">
                      <img
                        className="w-16 h-16 rounded-full"
                        src={row.logo.signedUrl}
                        alt=""
                      ></img>
                    </div>
                  </TableCell>
                  <TableCell>
                    <SCLabel fontWeight="medium" align="left">
                      {row.name}
                    </SCLabel>
                  </TableCell>
                  <TableCell>
                    <SCLabel fontWeight="medium" align="left">
                      {row.status}
                    </SCLabel>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignContent: "center",
                        justifyContent: "center",
                      }}
                    >
                      {/* <Button
                        size="medium"
                        variant="contained"
                        color="success"
                        sx={{ textTransform: "none" }}
                        onClick={() => handleOpenDialog(row, "approve")}
                      >
                        <Beenhere />
                        &nbsp;Approve
                      </Button>
                      <Button
                        size="medium"
                        variant="contained"
                        color="error"
                        sx={{ textTransform: "none" }}
                        onClick={() => handleOpenDialog(row, "reject")}
                      >
                        <ThumbDown />
                        &nbsp;Reject
                      </Button>  */}
                      <button onClick={() => handleViewBusiness(row)}>
                        <div className="flex items-center justify-between gap-4 border bg-gray-950 border-white p-2 px-4 rounded-full drop-shadow-2xl">
                          <EyeIcon className="text-white w-5"></EyeIcon>
                          <span className="font-Poppins  font-semibold uppercase text-white">
                            View
                          </span>
                        </div>
                      </button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            backgroundColor: "#2C2C2C",
            color: "#FFFFFF",
            borderTop: "1px solid #3F3F3F",
          }}
        />
      </Box>

      <Dialog
        open={openViewerDialog}
        onClose={handleCloseViewer}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <DialogContent>
          <BusinessViewer
            onClose={handleCloseViewer}
            businessId={business ? business.businessId : ""}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description"
      >
        <DialogTitle id="confirmation-dialog-title">
          {actionType === "approve" ? "Confirm Approval" : "Confirm Rejection"}
        </DialogTitle>
        <DialogContent>
          Are you sure you want to {actionType} the business&nbsp;
          <SCLabel color="#000">{selectedRow?.name}?</SCLabel>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            size="small"
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            size="small"
            variant="contained"
            color="success"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingModal
        message="Loading businesses, please hold on!"
        open={loading}
      />
    </Box>
  );
};

export default BusinessList;
